import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentShop,
  hasPermission,
  getCurrentUser,
  isOwnerInShopMode,
} from "@/lib/auth";
import { Shield } from "lucide-react";
import { exportToExcel } from "@/lib/exportToExcel";
import {
  getShopInventory,
  getProducts,
  createProduct,
  createInventoryItem,
  updateInventoryItem,
  deleteProduct,
  getShopInventoryPaginated,
  getInventoryCalculations,
  getAuthHeaders,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Package,
  BarChart3,
  Truck,
  FileText,
  WifiOff,
  RefreshCw,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Bell, Edit2, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { Inventory, InsertProduct } from "@shared/schema";
import { PRODUCT_CATEGORIES } from "@shared/categories";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getShopOwnerCurrencySync } from "@/lib/auth";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import AnalysisTab from "@/components/inventory/AnalysisTab";
import SuppliersTab from "@/components/inventory/SuppliersTab";
import ReportsTab from "@/components/inventory/ReportsTab";
import SupplierSelector from "@/components/inventory/SupplierSelector";
import SupplierModal from "@/components/inventory/SupplierModal";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  useOfflineInventory,
  useOfflineProducts,
} from "@/lib/hooks/use-offline-data";
import { indexedDBService } from "@/lib/db/indexed-db";
import { Badge } from "@/components/ui/badge";
import { SyncButton } from "@/components/inventory/SyncButton";
import { usePlan } from "@/hooks/usePlan";

// ============ TIPO PARA PROVEEDORES ============
interface Supplier {
  id: string;
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  taxId?: string;
  paymentTerms?: string;
  deliveryTime?: number;
  rating?: number;
  notes?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Función para obtener símbolo de moneda
const getCurrencySymbol = (): string => {
  const currency = getShopOwnerCurrencySync();
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    CUP: "$",
    L: "L",
    MXN: "$",
  };
  return symbols[currency] || "$";
};

// Componente para mostrar información de columnas en móvil
const ColumnInfo = () => (
  <div className="md:hidden mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
    <p className="text-sm font-medium text-blue-800 mb-1">
      ℹ️ Información visible en móvil:
    </p>
    <p className="text-xs text-blue-600">
      <strong>Producto, Precio, Stock, Acciones</strong>
    </p>
    <p className="text-xs text-blue-600 mt-1">
      Usa tablet o computadora para ver:{" "}
      <strong>Costo, Margen, Proveedor, Lotes, Fechas de caducidad</strong>
    </p>
  </div>
);

// ============================================================================
// MODAL: Agregar Producto (definido ANTES de usarlo)
// ============================================================================
function AddProductDialog({
  open,
  onOpenChange,
  mutation,
  isOfflineMode,
  planCan,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mutation: any;
  isOfflineMode: boolean;
  planCan: any;
}) {
  const queryClient = useQueryClient();
  const currentShop = getCurrentShop()!;

  const [name, setName] = useState("");
  const [category, setCategory] = useState<string>("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [threshold, setThreshold] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierNotes, setSupplierNotes] = useState("");
  const [acquisitionCost, setAcquisitionCost] = useState("");
  const [profitMargin, setProfitMargin] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [purchaseDate, setPurchaseDate] = useState<Date>();
  const [expiryDate, setExpiryDate] = useState<Date>();
  const [minimumOrderQuantity, setMinimumOrderQuantity] = useState("1");
  const [leadTimeDays, setLeadTimeDays] = useState("7");
  const [dailySalesRate, setDailySalesRate] = useState("0.00");
  const [reorderPoint, setReorderPoint] = useState("");
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);

  // 🟢 CORRECCIÓN: Añadidos estados para sugerencias
  const [priceSuggestion, setPriceSuggestion] = useState<string>("");
  const [marginSuggestion, setMarginSuggestion] = useState<string>("");
  // ============ NUEVO: Estado local para controlar el guardado ============
  const [isSaving, setIsSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<{
    price?: string;
    stock?: string;
    threshold?: string;
  }>({});

  useEffect(() => {
    if (open) {
      setErrors({});
    }
  }, [open]);

  // ============ VERIFICAR QUE LA PROP ISOFFLINEMODE SE RECIBE ============
  useEffect(() => {}, [isOfflineMode]);

  const validateFields = (): boolean => {
    const newErrors: typeof errors = {};

    // {Validación de precio}
    if (!price.trim() && !priceSuggestion) {
      newErrors.price = "El precio es requerido";
    } else if (price.trim()) {
      const priceNum = Number(price);
      if (isNaN(priceNum) || priceNum <= 0) {
        newErrors.price = "El precio debe ser un número mayor a 0";
      }
    } else if (priceSuggestion) {
      const priceNum = Number(priceSuggestion);
      if (isNaN(priceNum) || priceNum <= 0) {
        newErrors.price = "El precio sugerido no es válido";
      }
    }

    // {Validación de stock - AHORA PERMITE 0}
    if (!stock.trim() && stock !== "0") {
      newErrors.stock = "El stock es requerido";
    } else if (!/^\d+$/.test(stock)) {
      newErrors.stock = "El stock debe ser un número entero (ej: 10)";
    } else if (Number(stock) < 0) {
      newErrors.stock = "El stock no puede ser negativo";
    }

    // {Validación de umbral - AHORA PERMITE CUALQUIER VALOR POSITIVO}
    if (!threshold.trim() && threshold !== "0") {
      newErrors.threshold = "La alerta mínima es requerida";
    } else if (!/^\d+$/.test(threshold)) {
      newErrors.threshold =
        "La alerta mínima debe ser un número entero (ej: 5)";
    } else if (Number(threshold) <= 0) {
      newErrors.threshold = "La alerta mínima debe ser mayor a 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (stayOpen = false) => {
    setErrors({});

    const priceToUse = price.trim() ? price : priceSuggestion;
    const marginToUse = profitMargin.trim() ? profitMargin : marginSuggestion;

    if (!priceToUse || priceToUse === "0.00") {
      console.log("❌ Error: precio requerido");
      toast({
        title: "Error",
        description: "El precio es requerido",
        variant: "destructive",
      });
      return;
    }

    if (!category) {
      console.log("❌ Error: categoría requerida");
      toast({
        title: "Error",
        description: "Debes seleccionar una categoría",
        variant: "destructive",
      });
      return;
    }

    if (!name.trim()) {
      console.log("❌ Error: nombre requerido");
      toast({
        title: "Error",
        description: "El nombre del producto es requerido",
        variant: "destructive",
      });
      return;
    }

    if (priceToUse !== price) {
      setPrice(priceToUse);
    }
    if (marginToUse && marginToUse !== profitMargin) {
      setProfitMargin(marginToUse);
    }

    if (!validateFields()) {
      console.log("❌ Error: validación de campos falló");
      toast({
        title: "Error de validación",
        description: "Por favor corrige los errores en los campos",
        variant: "destructive",
      });
      return;
    }

    // ============ NUEVO: Estado local de carga ============
    setIsSaving(true);

    try {
      // Preparar datos para la mutación
      const productData = {
        name,
        category,
        price: priceToUse,
        stock: Number(stock),
        threshold: Number(threshold),
        supplierId: supplierId || undefined,
        supplierNotes: supplierNotes || undefined,
        acquisitionCost: acquisitionCost || undefined,
        profitMargin: profitMargin || undefined,
        batchNumber: batchNumber || undefined,
        purchaseDate: purchaseDate?.toISOString(),
        expiryDate: expiryDate?.toISOString(),
        minimumOrderQuantity: minimumOrderQuantity
          ? Number(minimumOrderQuantity)
          : undefined,
        leadTimeDays: leadTimeDays ? Number(leadTimeDays) : undefined,
        dailySalesRate: dailySalesRate || undefined,
        reorderPoint: reorderPoint ? Number(reorderPoint) : undefined,
        priceSuggestion: priceSuggestion || undefined,
        marginSuggestion: marginSuggestion || undefined,
      };

      // Usar la mutación del padre (recibida como prop)
      const result = await mutation.mutateAsync(productData);

      // Limpiar formulario
      setName("");
      setCategory("");
      setPrice("");
      setStock("");
      setThreshold("");
      setSupplierId("");
      setSupplierNotes("");
      setAcquisitionCost("");
      setProfitMargin("");
      setBatchNumber("");
      setPurchaseDate(undefined);
      setExpiryDate(undefined);
      setMinimumOrderQuantity("1");
      setLeadTimeDays("7");
      setDailySalesRate("0.00");
      setReorderPoint("");
      setShowAdvancedFields(false);
      setPriceSuggestion("");
      setMarginSuggestion("");

      if (!stayOpen) {
        onOpenChange(false);
      } else {
        setTimeout(() => nameInputRef.current?.focus(), 0);
      }
    } catch (error) {
      console.error("❌ Error en mutación:", error);
      toast({
        title: "Error",
        description: "No se pudo crear el producto",
        variant: "destructive",
      });
    } finally {
      // ============ NUEVO: Quitar estado de carga ============
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setName("");
    setCategory("");
    setPrice("");
    setStock("");
    setThreshold("");
    setSupplierId("");
    setSupplierNotes("");
    setAcquisitionCost("");
    setProfitMargin("");
    setBatchNumber("");
    setPurchaseDate(undefined);
    setExpiryDate(undefined);
    setMinimumOrderQuantity("1");
    setLeadTimeDays("7");
    setShowAdvancedFields(false);
    setErrors({});
    // 🟢 CORRECCIÓN: Limpiar sugerencias
    setPriceSuggestion("");
    setMarginSuggestion("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetForm();
        }
        onOpenChange(isOpen);
      }}
    >
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Agregar producto</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* ============ INDICADOR DE MODO OFFLINE EN EL MODAL ============ */}
          {isOfflineMode && (
            <div className="mb-4 p-2 bg-amber-100 border border-amber-300 rounded-md">
              <p className="text-xs text-amber-800 flex items-center gap-1">
                <WifiOff className="h-3 w-3" />
                <span>Modo offline - El producto se guardará localmente</span>
              </p>
            </div>
          )}
          {/* Campos básicos */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
              <Label className="sm:text-right">Nombre *</Label>
              <Input
                ref={nameInputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="sm:col-span-3"
                placeholder="Nombre del producto"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
              <Label className="sm:text-right">Categoría *</Label>
              <div className="sm:col-span-3">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {PRODUCT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
              <Label className="sm:text-right">
                Precio ({getShopOwnerCurrencySync()}) *
              </Label>
              <div className="sm:col-span-3 space-y-1">
                <Input
                  value={price}
                  onFocus={(e) => {
                    if (e.target.value === "0" || e.target.value === "0.00") {
                      e.target.value = "";
                    }
                  }}
                  onChange={(e) => {
                    const newPrice = e.target.value;
                    setPrice(newPrice);

                    // 🟢 CORRECCIÓN: Calcular margen si hay costo
                    if (newPrice && acquisitionCost && !profitMargin) {
                      const costNum = parseFloat(acquisitionCost);
                      const priceNum = parseFloat(newPrice);
                      if (!isNaN(costNum) && !isNaN(priceNum) && costNum > 0) {
                        const calculatedMargin = (
                          ((priceNum - costNum) / costNum) *
                          100
                        ).toFixed(2);
                        setMarginSuggestion(calculatedMargin);
                        setPriceSuggestion(""); // Limpiar sugerencia de precio
                      }
                    } else {
                      setMarginSuggestion("");
                      setPriceSuggestion("");
                    }

                    if (errors.price)
                      setErrors((prev) => ({ ...prev, price: undefined }));
                  }}
                  className={`${errors.price ? "border-destructive" : ""}`}
                  placeholder="Ej: 10.50"
                />
                {priceSuggestion &&
                  !price.trim() &&
                  acquisitionCost &&
                  profitMargin && (
                    <div className="text-xs text-green-600 bg-green-50 p-2 rounded border border-green-200 mt-1">
                      <div className="flex items-center justify-between">
                        <span>
                          💡 <strong>Precio sugerido:</strong>{" "}
                          {getCurrencySymbol()}
                          {priceSuggestion}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            setPrice(priceSuggestion);
                            setPriceSuggestion("");
                          }}
                        >
                          Usar
                        </Button>
                      </div>
                      <div className="text-green-700 mt-1">
                        Cálculo: {acquisitionCost} × (1 + {profitMargin}%) ={" "}
                        {getCurrencySymbol()}
                        {priceSuggestion}
                      </div>
                    </div>
                  )}
                {marginSuggestion &&
                  !profitMargin.trim() &&
                  acquisitionCost &&
                  price && (
                    <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200 mt-1">
                      <div className="flex items-center justify-between">
                        <span>
                          📊 <strong>Margen sugerido:</strong>{" "}
                          {marginSuggestion}%
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            setProfitMargin(marginSuggestion);
                            setMarginSuggestion("");
                          }}
                        >
                          Usar
                        </Button>
                      </div>
                      <div className="text-blue-700 mt-1">
                        Cálculo: (({price} - {acquisitionCost}) ÷{" "}
                        {acquisitionCost}) × 100 = {marginSuggestion}%
                      </div>
                    </div>
                  )}
                {errors.price && (
                  <p className="text-sm text-destructive">{errors.price}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
              <Label className="sm:text-right">Stock inicial *</Label>
              <div className="sm:col-span-3 space-y-1">
                <Input
                  value={stock}
                  onFocus={(e) => {
                    if (e.target.value === "0") {
                      e.target.value = "";
                    }
                  }}
                  onChange={(e) => {
                    setStock(e.target.value);
                    if (errors.stock)
                      setErrors((prev) => ({ ...prev, stock: undefined }));
                  }}
                  className={`${errors.stock ? "border-destructive" : ""}`}
                  placeholder="Ej: 100"
                />
                {errors.stock && (
                  <p className="text-sm text-destructive">{errors.stock}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
              <Label className="sm:text-right">Alerta mínima *</Label>
              <div className="sm:col-span-3 space-y-1">
                <Input
                  value={threshold}
                  onFocus={(e) => {
                    if (e.target.value === "0") {
                      e.target.value = "";
                    }
                  }}
                  onChange={(e) => {
                    setThreshold(e.target.value);
                    if (errors.threshold)
                      setErrors((prev) => ({ ...prev, threshold: undefined }));
                  }}
                  className={`${errors.threshold ? "border-destructive" : ""}`}
                  placeholder="Ej: 10"
                />
                {errors.threshold && (
                  <p className="text-sm text-destructive">{errors.threshold}</p>
                )}
                {/* {NUEVO: Mensaje informativo cuando umbral > stock} */}
                {!errors.threshold &&
                  threshold &&
                  stock &&
                  Number(threshold) > Number(stock) && (
                    <p className="text-xs text-amber-600">
                      ⚠️ La alerta mínima ({threshold}) es mayor que el stock
                      inicial ({stock})
                    </p>
                  )}
              </div>
            </div>
          </div>

          {/* Botón campos avanzados - AHORA CON VERIFICACIÓN DE PLAN */}
          <div className="flex justify-center pt-2">
            {!planCan.useAdvancedInventory() ? (
              <div className="relative group w-full">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2 opacity-50 cursor-not-allowed w-full"
                  disabled
                >
                  {showAdvancedFields
                    ? "Ocultar campos avanzados"
                    : "Mostrar campos avanzados"}
                  <span className="text-xs">
                    {showAdvancedFields ? "↑" : "↓"}
                  </span>
                </Button>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-amber-100 border border-amber-300 rounded-md text-xs text-amber-800 shadow-lg z-50 hidden group-hover:block">
                  <p className="font-medium">🔒 Bloqueado por plan</p>
                  <p>
                    Los campos avanzados de inventario (proveedores, lotes,
                    fechas de caducidad, etc.) requieren plan Profesional o
                    Enterprise.
                  </p>
                  <p className="mt-1 text-amber-700">
                    <Button
                      variant="link"
                      className="text-xs text-amber-900 underline p-0 h-auto"
                      onClick={() => (window.location.href = "/owner/plans")}
                    >
                      Mejorar plan
                    </Button>
                  </p>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                className="gap-2"
              >
                {showAdvancedFields
                  ? "Ocultar campos avanzados"
                  : "Mostrar campos avanzados"}
                <span className="text-xs">
                  {showAdvancedFields ? "↑" : "↓"}
                </span>
              </Button>
            )}
          </div>

          {/* Campos avanzados */}
          {showAdvancedFields && (
            <div className="space-y-4 pt-4 border-t">
              {!planCan.useAdvancedInventory() ? (
                <div className="text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="font-medium text-gray-700 mb-2">
                    Campos Avanzados Bloqueados
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    La gestión avanzada de inventario (proveedores, lotes,
                    fechas de caducidad, etc.) está disponible en los planes
                    Profesional y Enterprise.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => (window.location.href = "/owner/plans")}
                    className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                  >
                    Mejorar Plan
                  </Button>
                </div>
              ) : (
                <>
                  {/* Proveedor - usando selector en lugar de campos de texto */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                    <Label className="sm:text-right pt-2">Proveedor</Label>
                    <div className="sm:col-span-3">
                      <SupplierSelector
                        shopId={currentShop.id}
                        value={supplierId}
                        onValueChange={(value) => setSupplierId(value)}
                        placeholder="Seleccionar proveedor..."
                        label=""
                      />
                    </div>
                  </div>

                  {/* Notas proveedor (mantenemos este campo) */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                    <Label className="sm:text-right">Notas proveedor</Label>
                    <Textarea
                      value={supplierNotes}
                      onChange={(e) => setSupplierNotes(e.target.value)}
                      className="sm:col-span-3"
                      placeholder="Notas adicionales sobre el proveedor"
                      rows={2}
                    />
                  </div>

                  {/* Costo adquisición */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                    <Label className="sm:text-right">
                      Costo adquisición ({getShopOwnerCurrencySync()})
                    </Label>
                    <div className="sm:col-span-3 space-y-1">
                      <Input
                        value={acquisitionCost}
                        onChange={(e) => {
                          const newCost = e.target.value;
                          setAcquisitionCost(newCost);

                          // 🟢 CORRECCIÓN: Mostrar sugerencia de precio si hay margen
                          if (newCost && profitMargin) {
                            const costNum = parseFloat(newCost);
                            const marginNum = parseFloat(profitMargin);
                            if (
                              !isNaN(costNum) &&
                              !isNaN(marginNum) &&
                              costNum > 0
                            ) {
                              const suggested = (
                                costNum *
                                (1 + marginNum / 100)
                              ).toFixed(2);
                              setPriceSuggestion(suggested);
                              setMarginSuggestion(""); // Limpiar sugerencia de margen
                            }
                          }
                          // 🟢 CORRECCIÓN: Calcular margen si hay precio
                          else if (newCost && price && !profitMargin) {
                            const costNum = parseFloat(newCost);
                            const priceNum = parseFloat(price);
                            if (
                              !isNaN(costNum) &&
                              !isNaN(priceNum) &&
                              costNum > 0
                            ) {
                              const calculatedMargin = (
                                ((priceNum - costNum) / costNum) *
                                100
                              ).toFixed(2);
                              setMarginSuggestion(calculatedMargin);
                              setPriceSuggestion(""); // Limpiar sugerencia de precio
                            }
                          } else {
                            setPriceSuggestion("");
                            setMarginSuggestion("");
                          }
                        }}
                        className="sm:col-span-3"
                        placeholder="Precio al que compras"
                      />
                    </div>
                  </div>

                  {/* Margen (%) */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                    <Label className="sm:text-right">Margen (%)</Label>
                    <div className="sm:col-span-3 space-y-1">
                      <Input
                        value={profitMargin}
                        onChange={(e) => {
                          const newMargin = e.target.value;
                          setProfitMargin(newMargin);

                          // 🟢 CORRECCIÓN: Mostrar sugerencia de precio si hay costo
                          if (newMargin && acquisitionCost) {
                            const costNum = parseFloat(acquisitionCost);
                            const marginNum = parseFloat(newMargin);
                            if (
                              !isNaN(costNum) &&
                              !isNaN(marginNum) &&
                              costNum > 0
                            ) {
                              const suggested = (
                                costNum *
                                (1 + marginNum / 100)
                              ).toFixed(2);
                              setPriceSuggestion(suggested);
                              setMarginSuggestion(""); // Limpiar sugerencia de margen
                            }
                          } else {
                            setPriceSuggestion("");
                            setMarginSuggestion("");
                          }
                        }}
                        className="sm:col-span-3"
                        placeholder="Ej: 30 para 30%"
                      />
                    </div>
                  </div>

                  {/* Número de lote */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                    <Label className="sm:text-right">Número de lote</Label>
                    <Input
                      value={batchNumber}
                      onChange={(e) => setBatchNumber(e.target.value)}
                      className="sm:col-span-3"
                      placeholder="Ej: LOTE-2024-001"
                    />
                  </div>

                  {/* Fecha compra */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                    <Label className="sm:text-right pt-2">Fecha compra</Label>
                    <div className="sm:col-span-3">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !purchaseDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {purchaseDate
                              ? format(purchaseDate, "PPP", { locale: es })
                              : "Seleccionar fecha"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={purchaseDate}
                            onSelect={setPurchaseDate}
                            initialFocus
                            locale={es}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Fecha caducidad */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                    <Label className="sm:text-right pt-2">
                      Fecha caducidad
                    </Label>
                    <div className="sm:col-span-3">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !expiryDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {expiryDate
                              ? format(expiryDate, "PPP", { locale: es })
                              : "Seleccionar fecha"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={expiryDate}
                            onSelect={setExpiryDate}
                            initialFocus
                            locale={es}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Cant. mínima pedido */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                    <Label className="sm:text-right">Cant. mínima pedido</Label>
                    <Input
                      type="number"
                      min="1"
                      value={minimumOrderQuantity}
                      onChange={(e) => setMinimumOrderQuantity(e.target.value)}
                      className="sm:col-span-3"
                    />
                  </div>

                  {/* Tiempo entrega (días) */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                    <Label className="sm:text-right">
                      Tiempo entrega (días)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={leadTimeDays}
                      onChange={(e) => setLeadTimeDays(e.target.value)}
                      className="sm:col-span-3"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={() => handleSave(true)} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              "Guardar y otro"
            )}
          </Button>
          <Button onClick={() => handleSave(false)} disabled={isSaving}>
            Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MODAL: Editar Producto (definido ANTES de usarlo)
// ============================================================================
function EditDialog({
  item,
  productName,
  productCategory: initialProductCategory,
  isOfflineMode,
}: {
  item: Inventory;
  productName: string;
  productCategory: string;
  isOfflineMode: boolean;
}) {
  const queryClient = useQueryClient();
  const currentShop = getCurrentShop();

  if (!currentShop) {
    toast({
      title: "Error",
      description: "No se pudo identificar la tienda",
      variant: "destructive",
    });
    return null;
  }

  // {NUEVO: Estados para nombre y categoría}
  const [productNameState, setProductNameState] = useState(productName);
  const [productCategory, setProductCategory] = useState<string>(
    initialProductCategory,
  );

  const [price, setPrice] = useState(item.price);
  const [stock, setStock] = useState(item.stock);
  const [threshold, setThreshold] = useState(item.lowStockThreshold);
  const [open, setOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Estados para campos avanzados
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [supplierId, setSupplierId] = useState(item.supplierId || "");
  // 🟢 CORRECCIÓN: Añadidos estados para sugerencias
  const [priceSuggestion, setPriceSuggestion] = useState<string>("");
  const [marginSuggestion, setMarginSuggestion] = useState<string>("");

  // Obtener información del plan
  const { can } = usePlan();

  // 🟢 CORRECCIÓN: Resetear sugerencias cuando se abre/cierra el diálogo
  useEffect(() => {
    if (open) {
      setPriceSuggestion("");
      setMarginSuggestion("");
    }
  }, [open]);

  const [supplierNotes, setSupplierNotes] = useState(item.supplierNotes || "");
  const [acquisitionCost, setAcquisitionCost] = useState(
    item.acquisitionCost || "",
  );
  const [profitMargin, setProfitMargin] = useState(item.profitMargin || "");
  const [batchNumber, setBatchNumber] = useState(item.batchNumber || "");
  const [purchaseDate, setPurchaseDate] = useState<Date | undefined>(
    item.purchaseDate ? new Date(item.purchaseDate) : undefined,
  );
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(
    item.expiryDate ? new Date(item.expiryDate) : undefined,
  );
  const [minimumOrderQuantity, setMinimumOrderQuantity] = useState(
    item.minimumOrderQuantity?.toString() || "1",
  );
  const [leadTimeDays, setLeadTimeDays] = useState(
    item.leadTimeDays?.toString() || "7",
  );
  const [dailySalesRate, setDailySalesRate] = useState(
    item.dailySalesRate || "0.00",
  );
  const [reorderPoint, setReorderPoint] = useState(
    item.reorderPoint?.toString() || "",
  );

  const updateMutation = useMutation({
    mutationFn: async () => {
      // ============ DETECTAR SI ESTAMOS OFFLINE ============
      const isOffline = isOfflineMode;

      // Preparar las actualizaciones
      const updates: any = {
        price: price.toString(),
        stock: Number(stock),
        lowStockThreshold: Number(threshold),
      };

      // {NUEVO: Agregar nombre y categoría si cambiaron}
      if (productNameState !== productName) {
        updates.productName = productNameState;
      }
      if (productCategory !== initialProductCategory) {
        updates.productCategory = productCategory;
      }

      // 🟢 CORRECCIÓN: Usar precio sugerido si no hay precio ingresado
      if (!price && priceSuggestion) {
        updates.price = priceSuggestion;
        console.log(
          "💰 Usando precio sugerido en actualización:",
          priceSuggestion,
        );
      }

      // 🟢 CORRECCIÓN: Usar margen sugerido si no hay margen ingresado
      if (!profitMargin && marginSuggestion) {
        updates.profitMargin = marginSuggestion;
        console.log(
          "📊 Usando margen sugerido en actualización:",
          marginSuggestion,
        );
      }

      // Agregar campos avanzados si existen
      if (supplierId === "" || supplierId === "null" || supplierId === null) {
        updates.supplierId = null;
        console.log("🔄 supplierId establecido como null para actualización");
      } else if (supplierId && supplierId.trim()) {
        updates.supplierId = supplierId;
        console.log("✅ supplierId incluido:", supplierId);
      }

      if (supplierNotes.trim()) updates.supplierNotes = supplierNotes;
      if (acquisitionCost.trim()) updates.acquisitionCost = acquisitionCost;
      if (profitMargin.trim()) updates.profitMargin = profitMargin;
      if (batchNumber.trim()) updates.batchNumber = batchNumber;
      if (purchaseDate) updates.purchaseDate = purchaseDate.toISOString();
      if (expiryDate) updates.expiryDate = expiryDate.toISOString();
      if (minimumOrderQuantity.trim())
        updates.minimumOrderQuantity = Number(minimumOrderQuantity);
      if (leadTimeDays.trim()) updates.leadTimeDays = Number(leadTimeDays);
      if (dailySalesRate.trim()) updates.dailySalesRate = dailySalesRate;
      if (reorderPoint.trim()) updates.reorderPoint = Number(reorderPoint);

      // Calcular precio sugerido si hay costo y margen
      if (acquisitionCost && profitMargin) {
        const cost = parseFloat(acquisitionCost);
        const margin = parseFloat(profitMargin);
        if (!isNaN(cost) && !isNaN(margin) && cost > 0) {
          updates.suggestedPrice = (cost * (1 + margin / 100)).toFixed(2);
        }
      }

      // ============ MODO OFFLINE ============
      if (isOffline) {
        console.log("📴 Modo offline: guardando actualización localmente");

        try {
          // Crear operación pendiente
          const pendingOp = {
            id: `op_${Date.now()}`,
            type: "update" as const,
            entityType: "inventory" as const,
            data: {
              id: item.id,
              updates,
            },
            timestamp: new Date().toISOString(),
            retryCount: 0,
            priority: "normal" as const,
            originalCreatedAt: new Date().toISOString(),
            status: "pending" as const,
          };

          await indexedDBService.addPendingOperation(pendingOp);
          console.log("✅ Operación pendiente guardada para actualización");

          // Actualizar en IndexedDB
          const updatedItem = {
            ...item,
            ...updates,
            lastUpdated: new Date().toISOString(),
          };

          await indexedDBService.saveInventoryItem(updatedItem);
          console.log("✅ Item actualizado en IndexedDB");

          // ============ DISPARAR EVENTOS PARA ACTUALIZAR UI ============
          window.dispatchEvent(new CustomEvent("inventory-changed"));
          window.dispatchEvent(new CustomEvent("products-changed")); // ← NUEVO
          window.dispatchEvent(new CustomEvent("pending-changed"));

          toast({
            title: "📴 Producto actualizado localmente",
            description: "Se sincronizará cuando tengas conexión",
            duration: 5000,
          });

          return { success: true, offline: true };
        } catch (error) {
          console.error("❌ Error guardando actualización offline:", error);
          toast({
            title: "Error",
            description: "No se pudo guardar la actualización localmente",
            variant: "destructive",
          });
          throw error;
        }
      }

      // ============ MODO ONLINE ============
      console.log("🌐 Ejecutando actualización online");
      return updateInventoryItem(item.id, updates);
    },
    onSuccess: (result) => {
      setOpen(false);

      if (result?.offline) {
        console.log("✅ Actualización offline completada");
        toast({
          title: "📴 Producto actualizado localmente",
          description: "Se sincronizará cuando tengas conexión",
        });
      } else {
        toast({
          title: "✅ Producto actualizado",
          description: "Los cambios se han guardado correctamente.",
        });
      }

      // {CRÍTICO: Disparar eventos para forzar actualización en AMBOS modos}
      window.dispatchEvent(new CustomEvent("inventory-changed"));
      window.dispatchEvent(new CustomEvent("products-changed"));

      // {Invalidar todas las queries relacionadas}
      queryClient.invalidateQueries({
        queryKey: ["shopInventory", currentShop?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["inventoryCalculations", currentShop?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });

      // {Forzar refetch inmediato de todas las queries}
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ["shopInventory", currentShop?.id],
        });
        queryClient.refetchQueries({
          queryKey: ["inventoryCalculations", currentShop?.id],
        });
        queryClient.refetchQueries({
          queryKey: ["products"],
        });

        console.log(
          "🔄 Refetch automático ejecutado después de actualizar producto",
        );
      }, 100);
    },
    onError: (error) => {
      console.error("❌ Error en actualización:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el producto",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!currentShop) {
        throw new Error("No se pudo identificar la tienda");
      }

      // Obtener headers de autenticación
      const authHeaders = getAuthHeaders();

      const inventoryRes = await fetch(`/api/inventory/${item.id}`, {
        method: "DELETE",
        headers: {
          ...authHeaders, // ← AGREGAR HEADERS DE AUTENTICACIÓN
        },
      });

      if (!inventoryRes.ok) {
        const errorData = await inventoryRes.json();
        console.error("❌ Error inventario:", errorData);
        throw new Error(errorData.error || "Failed to delete inventory item");
      }

      const productRes = await fetch(`/api/products/${item.productId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders, // ← AGREGAR HEADERS DE AUTENTICACIÓN
        },
        body: JSON.stringify({
          shopId: currentShop.id,
        }),
      });

      if (!productRes.ok) {
        const errorData = await productRes.json();
        console.error("❌ Error producto:", errorData);
        throw new Error(errorData.error || "Failed to delete product");
      }
      return true;
    },
    onSuccess: () => {
      setOpen(false);

      // {CRÍTICO: Disparar eventos para forzar actualización inmediata}
      window.dispatchEvent(new CustomEvent("inventory-changed"));
      window.dispatchEvent(new CustomEvent("products-changed"));

      // {Invalidar todas las queries relacionadas}
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({
        queryKey: ["shopInventory", currentShop?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({
        queryKey: ["inventoryCalculations", currentShop?.id],
      });

      // {Forzar refetch inmediato}
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ["shopInventory", currentShop?.id],
        });
        queryClient.refetchQueries({ queryKey: ["products"] });
        queryClient.refetchQueries({
          queryKey: ["inventoryCalculations", currentShop?.id],
        });

        console.log(
          "🔄 Refetch automático ejecutado después de eliminar producto",
        );
      }, 100);

      toast({
        title: "✅ Producto eliminado",
        description:
          "El producto se ha eliminado correctamente del inventario.",
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      console.error("💥 Error en eliminación:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el producto",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    // 🟢 CORRECCIÓN: Usar sugerencias si existen
    const priceToUse = price.trim() ? price : priceSuggestion;
    const marginToUse = profitMargin.trim() ? profitMargin : marginSuggestion;

    // {ELIMINADA: La validación que impedía guardar si umbral > stock}
    // {Ahora se permite guardar sin restricciones de comparación entre stock y umbral}

    // 🟢 CORRECCIÓN: Actualizar estados con sugerencias antes de enviar
    if (priceToUse && priceToUse !== price) {
      setPrice(priceToUse);
    }
    if (marginToUse && marginToUse !== profitMargin) {
      setProfitMargin(marginToUse);
    }

    // ============ DETECTAR SI ESTAMOS OFFLINE ============
    if (isOfflineMode) {
      console.log("📴 Modo offline: guardando actualización localmente");

      // Preparar las actualizaciones
      const updates: any = {
        price: priceToUse.toString(),
        stock: Number(stock),
        lowStockThreshold: Number(threshold),
      };

      // {NUEVO: Agregar nombre y categoría}
      if (productNameState !== productName) {
        updates.productName = productNameState;
      }
      if (productCategory !== initialProductCategory) {
        updates.productCategory = productCategory;
      }

      // Agregar campos avanzados si existen
      if (supplierId && supplierId.trim()) updates.supplierId = supplierId;
      if (supplierNotes.trim()) updates.supplierNotes = supplierNotes;
      if (acquisitionCost.trim()) updates.acquisitionCost = acquisitionCost;
      if (profitMargin.trim()) updates.profitMargin = profitMargin;
      if (batchNumber.trim()) updates.batchNumber = batchNumber;
      if (purchaseDate) updates.purchaseDate = purchaseDate.toISOString();
      if (expiryDate) updates.expiryDate = expiryDate.toISOString();
      if (minimumOrderQuantity.trim())
        updates.minimumOrderQuantity = Number(minimumOrderQuantity);
      if (leadTimeDays.trim()) updates.leadTimeDays = Number(leadTimeDays);
      if (dailySalesRate.trim()) updates.dailySalesRate = dailySalesRate;
      if (reorderPoint.trim()) updates.reorderPoint = Number(reorderPoint);

      // Verificar si ya existe una operación pendiente para este producto
      const checkExistingOperation = async () => {
        console.log(
          "🔍 Verificando operaciones existentes para producto:",
          item.id,
        );

        const operations = await indexedDBService.getPendingOperations();
        console.log(
          "📋 Todas las operaciones pendientes:",
          operations.map((op) => ({
            id: op.id,
            type: op.type,
            dataId: op.data?.id || op.data?.product?.id,
          })),
        );

        // Buscar operación CREATE existente
        const existingCreateOp = operations.find(
          (op) =>
            op.type === "create" &&
            op.data &&
            op.data.product &&
            op.data.product.id === item.productId,
        );

        // Buscar operación UPDATE existente
        const existingUpdateOp = operations.find(
          (op) => op.type === "update" && op.data && op.data.id === item.id,
        );

        console.log(
          "🔍 CREATE existente:",
          existingCreateOp ? existingCreateOp.id : "ninguna",
        );
        console.log(
          "🔍 UPDATE existente:",
          existingUpdateOp ? existingUpdateOp.id : "ninguna",
        );

        if (existingCreateOp) {
          // Si existe una operación CREATE, la convertimos en UPDATE
          console.log("🔄 Convirtiendo CREATE en UPDATE:", existingCreateOp.id);

          // Eliminar la operación CREATE
          await indexedDBService.removePendingOperation(existingCreateOp.id);

          // Crear nueva operación UPDATE
          const pendingOpId = `op_${Date.now()}`;
          const pendingOp = {
            id: pendingOpId,
            type: "update" as const,
            entityType: "inventory" as const,
            data: {
              id: item.id,
              updates,
            },
            timestamp: new Date().toISOString(),
            retryCount: 0,
            priority: "normal" as const,
            originalCreatedAt: new Date().toISOString(),
            status: "pending" as const,
          };

          await indexedDBService.addPendingOperation(pendingOp);
          console.log("✅ Nueva operación UPDATE creada:", pendingOpId);
        } else if (existingUpdateOp) {
          // Si ya existe una UPDATE, actualizarla
          console.log("🔄 Actualizando UPDATE existente:", existingUpdateOp.id);

          await indexedDBService.updatePendingOperation(existingUpdateOp.id, {
            data: {
              ...existingUpdateOp.data,
              updates: {
                ...existingUpdateOp.data.updates,
                ...updates,
              },
            },
            timestamp: new Date().toISOString(),
            retryCount: 0,
          });

          console.log("✅ UPDATE existente actualizada:", existingUpdateOp.id);
        } else {
          // No existe ninguna operación, crear nueva UPDATE
          console.log("🆕 Creando nueva operación UPDATE");

          const pendingOpId = `op_${Date.now()}`;
          const pendingOp = {
            id: pendingOpId,
            type: "update" as const,
            entityType: "inventory" as const,
            data: {
              id: item.id,
              updates,
            },
            timestamp: new Date().toISOString(),
            retryCount: 0,
            priority: "normal" as const,
            originalCreatedAt: new Date().toISOString(),
            status: "pending" as const,
          };

          await indexedDBService.addPendingOperation(pendingOp);
        }

        // Actualizar en IndexedDB
        const updatedItem = {
          ...item,
          ...updates,
          lastUpdated: new Date().toISOString(),
        };

        await indexedDBService.saveInventoryItem(updatedItem);
        console.log("✅ Item actualizado en IndexedDB");

        // Verificar conteo final
        const finalOps = await indexedDBService.getPendingOperations();
        console.log("📊 Operaciones después de editar:", finalOps.length);

        // Disparar eventos
        window.dispatchEvent(new CustomEvent("inventory-changed"));
        window.dispatchEvent(new CustomEvent("products-changed")); // ← AGREGADO
        window.dispatchEvent(new CustomEvent("pending-changed"));

        toast({
          title: "📴 Producto actualizado localmente",
          description: "Se sincronizará cuando tengas conexión",
          duration: 5000,
        });

        setOpen(false);
      };

      checkExistingOperation();
      return;
    }

    // 🟢 CORRECCIÓN: Pequeña pausa para que React actualice los estados
    setTimeout(() => {
      updateMutation.mutate();
    }, 100);
  };

  const handleDelete = () => {
    // ============ DETECTAR SI ESTAMOS OFFLINE ============
    if (isOfflineMode) {
      console.log("📴 Modo offline: guardando eliminación localmente");

      // Verificar si ya existe una operación pendiente para este producto
      const checkExistingOperation = async () => {
        const operations = await indexedDBService.getPendingOperations();

        // Buscar operaciones existentes para este producto
        const existingCreateOp = operations.find(
          (op) =>
            op.type === "create" &&
            op.data &&
            op.data.product &&
            op.data.product.id === item.productId,
        );

        const existingUpdateOp = operations.find(
          (op) => op.type === "update" && op.data && op.data.id === item.id,
        );

        // Si hay operaciones de creación o actualización, eliminarlas primero
        if (existingCreateOp) {
          console.log(
            "🗑️ Eliminando operación de creación existente:",
            existingCreateOp.id,
          );
          await indexedDBService.removePendingOperation(existingCreateOp.id);
        }

        if (existingUpdateOp) {
          console.log(
            "🗑️ Eliminando operación de actualización existente:",
            existingUpdateOp.id,
          );
          await indexedDBService.removePendingOperation(existingUpdateOp.id);
        }

        // Crear operación de eliminación
        const pendingOp = {
          id: `op_${Date.now()}`,
          type: "delete" as const,
          entityType: "inventory" as const,
          data: {
            id: item.id,
            productId: item.productId,
            isTemporal: item.id.toString().startsWith("temp_"),
          },
          timestamp: new Date().toISOString(),
          retryCount: 0,
          priority: "normal" as const,
          originalCreatedAt: new Date().toISOString(),
          status: "pending" as const,
        };

        await indexedDBService.addPendingOperation(pendingOp);

        // Eliminar de IndexedDB
        await indexedDBService.deleteInventoryItem(item.id);
        await indexedDBService.deleteProduct(item.productId);

        // Disparar eventos
        window.dispatchEvent(new CustomEvent("inventory-changed"));
        window.dispatchEvent(new CustomEvent("pending-changed"));

        toast({
          title: "📴 Producto marcado para eliminar",
          description: "Se eliminará cuando tengas conexión",
          duration: 5000,
        });

        setOpen(false);
      };

      checkExistingOperation();
      return;
    }

    // Modo online: eliminar directamente
    deleteMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid={`button-edit-${item.id}`}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Gestionar {productName}</DialogTitle>
        </DialogHeader>

        {showDeleteConfirm ? (
          <div className="space-y-4 py-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <h3 className="font-bold text-destructive mb-2">
                ¿Estás seguro de eliminar este producto?
              </h3>
              <p className="text-sm text-muted-foreground">
                Esta acción eliminará permanentemente el producto "{productName}
                ". Si hay items en inventario asociados, no podrás eliminarlo.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteMutation.isPending}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="w-full sm:w-auto"
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  "Sí, eliminar producto"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* Campos básicos */}
              <div className="space-y-4">
                {/* {NUEVO: Campo para nombre del producto} */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                  <Label className="sm:text-right">Nombre *</Label>
                  <Input
                    value={productNameState}
                    onChange={(e) => setProductNameState(e.target.value)}
                    className="sm:col-span-3"
                    placeholder="Nombre del producto"
                  />
                </div>

                {/* {NUEVO: Campo para categoría} */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                  <Label className="sm:text-right">Categoría *</Label>
                  <div className="sm:col-span-3">
                    <Select
                      value={productCategory}
                      onValueChange={setProductCategory}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una categoría" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {PRODUCT_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                  <Label className="sm:text-right">
                    Precio ({getShopOwnerCurrencySync()})
                  </Label>
                  <div className="sm:col-span-3 space-y-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={price}
                      onFocus={(e) => {
                        if (
                          e.target.value === "0" ||
                          e.target.value === "0.00"
                        ) {
                          e.target.value = "";
                        }
                      }}
                      onChange={(e) => {
                        const newPrice = e.target.value;
                        setPrice(newPrice);

                        // 🟢 CORRECCIÓN: Calcular margen si hay costo
                        if (newPrice && acquisitionCost && !profitMargin) {
                          const costNum = parseFloat(acquisitionCost);
                          const priceNum = parseFloat(newPrice);
                          if (
                            !isNaN(costNum) &&
                            !isNaN(priceNum) &&
                            costNum > 0
                          ) {
                            const calculatedMargin = (
                              ((priceNum - costNum) / costNum) *
                              100
                            ).toFixed(2);
                            setMarginSuggestion(calculatedMargin);
                            setPriceSuggestion(""); // Limpiar sugerencia de precio
                          }
                        } else {
                          setMarginSuggestion("");
                          setPriceSuggestion("");
                        }
                      }}
                      className="sm:col-span-3"
                    />
                    {priceSuggestion &&
                      !price.trim() &&
                      acquisitionCost &&
                      profitMargin && (
                        <div className="text-xs text-green-600 bg-green-50 p-2 rounded border border-green-200 mt-1">
                          <div className="flex items-center justify-between">
                            <span>
                              💡 <strong>Precio sugerido:</strong>{" "}
                              {getCurrencySymbol()}
                              {priceSuggestion}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => {
                                setPrice(priceSuggestion);
                                setPriceSuggestion("");
                              }}
                            >
                              Usar
                            </Button>
                          </div>
                          <div className="text-green-700 mt-1">
                            Cálculo: {acquisitionCost} × (1 + {profitMargin}%) ={" "}
                            {getCurrencySymbol()}
                            {priceSuggestion}
                          </div>
                        </div>
                      )}
                    {marginSuggestion &&
                      !profitMargin.trim() &&
                      acquisitionCost &&
                      price && (
                        <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200 mt-1">
                          <div className="flex items-center justify-between">
                            <span>
                              📊 <strong>Margen sugerido:</strong>{" "}
                              {marginSuggestion}%
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => {
                                setProfitMargin(marginSuggestion);
                                setMarginSuggestion("");
                              }}
                            >
                              Usar
                            </Button>
                          </div>
                          <div className="text-blue-700 mt-1">
                            Cálculo: (({price} - {acquisitionCost}) ÷{" "}
                            {acquisitionCost}) × 100 = {marginSuggestion}%
                          </div>
                        </div>
                      )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                  <Label className="sm:text-right">Stock</Label>
                  <div className="sm:col-span-3 space-y-1">
                    <Input
                      type="number"
                      value={stock}
                      onFocus={(e) => {
                        if (
                          e.target.value === "0" ||
                          e.target.value === "0.00"
                        ) {
                          e.target.value = "";
                        }
                      }}
                      onChange={(e) => setStock(Number(e.target.value))}
                      className="sm:col-span-3"
                    />
                    {/* {NUEVO: Mensaje informativo cuando stock < umbral} */}
                    {stock < threshold && (
                      <p className="text-xs text-amber-600">
                        ⚠️ El stock actual ({stock}) es menor que la alerta
                        mínima ({threshold})
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                  <Label className="sm:text-right">Alerta Stock</Label>
                  <div className="sm:col-span-3 space-y-1">
                    <Input
                      type="number"
                      value={threshold}
                      onFocus={(e) => {
                        if (e.target.value === "0") {
                          e.target.value = "";
                        }
                      }}
                      onChange={(e) => setThreshold(Number(e.target.value))}
                      className="sm:col-span-3"
                    />
                    {/* {NUEVO: Mensaje informativo cuando umbral > stock} */}
                    {threshold > stock && (
                      <p className="text-xs text-amber-600">
                        ⚠️ La alerta mínima ({threshold}) es mayor que el stock
                        actual ({stock})
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Botón campos avanzados */}
              <div className="flex justify-center pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                  className="gap-2"
                  disabled={!can.useAdvancedInventory()}
                >
                  {showAdvancedFields
                    ? "Ocultar campos avanzados"
                    : "Mostrar campos avanzados"}
                  <span className="text-xs">
                    {showAdvancedFields ? "↑" : "↓"}
                  </span>
                </Button>
              </div>

              {/* Campos avanzados */}
              {showAdvancedFields && (
                <div className="space-y-4 pt-4 border-t">
                  {!can.useAdvancedInventory() ? (
                    <div className="text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <h3 className="font-medium text-gray-700 mb-2">
                        Campos Avanzados Bloqueados
                      </h3>
                      <p className="text-sm text-gray-500 mb-4">
                        La gestión avanzada de inventario (proveedores, lotes,
                        fechas de caducidad, etc.) está disponible en los planes
                        Profesional y Enterprise.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => (window.location.href = "/owner/plans")}
                        className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                      >
                        Mejorar Plan
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-medium text-lg">
                        Información avanzada
                      </h3>
                      {/* Proveedor - usando selector */}
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                        <Label className="sm:text-right pt-2">Proveedor</Label>
                        <div className="sm:col-span-3">
                          <SupplierSelector
                            shopId={currentShop.id}
                            value={supplierId || "null"}
                            onValueChange={(value) =>
                              setSupplierId(value === "null" ? "" : value)
                            }
                            placeholder="Seleccionar proveedor..."
                            label=""
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">Notas proveedor</Label>
                        <Textarea
                          value={supplierNotes}
                          onChange={(e) => setSupplierNotes(e.target.value)}
                          className="sm:col-span-3"
                          placeholder="Notas adicionales"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">
                          Costo adquisición ({getShopOwnerCurrencySync()})
                        </Label>
                        <div className="sm:col-span-3 space-y-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={acquisitionCost}
                            onChange={(e) => {
                              const newCost = e.target.value;
                              setAcquisitionCost(newCost);

                              if (newCost && profitMargin) {
                                const costNum = parseFloat(newCost);
                                const marginNum = parseFloat(profitMargin);
                                if (
                                  !isNaN(costNum) &&
                                  !isNaN(marginNum) &&
                                  costNum > 0
                                ) {
                                  const suggested = (
                                    costNum *
                                    (1 + marginNum / 100)
                                  ).toFixed(2);
                                  setPriceSuggestion(suggested);
                                  setMarginSuggestion("");
                                }
                              } else if (newCost && price && !profitMargin) {
                                const costNum = parseFloat(newCost);
                                const priceNum = parseFloat(price);
                                if (
                                  !isNaN(costNum) &&
                                  !isNaN(priceNum) &&
                                  costNum > 0
                                ) {
                                  const calculatedMargin = (
                                    ((priceNum - costNum) / costNum) *
                                    100
                                  ).toFixed(2);
                                  setMarginSuggestion(calculatedMargin);
                                  setPriceSuggestion("");
                                }
                              } else {
                                setPriceSuggestion("");
                                setMarginSuggestion("");
                              }
                            }}
                            className="sm:col-span-3"
                            placeholder="Precio al que compras"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">Margen (%)</Label>
                        <div className="sm:col-span-3 space-y-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={profitMargin}
                            onChange={(e) => {
                              const newMargin = e.target.value;
                              setProfitMargin(newMargin);

                              if (newMargin && acquisitionCost) {
                                const costNum = parseFloat(acquisitionCost);
                                const marginNum = parseFloat(newMargin);
                                if (
                                  !isNaN(costNum) &&
                                  !isNaN(marginNum) &&
                                  costNum > 0
                                ) {
                                  const suggested = (
                                    costNum *
                                    (1 + marginNum / 100)
                                  ).toFixed(2);
                                  setPriceSuggestion(suggested);
                                  setMarginSuggestion("");
                                }
                              } else {
                                setPriceSuggestion("");
                                setMarginSuggestion("");
                              }
                            }}
                            className="sm:col-span-3"
                            placeholder="Ej: 30 para 30%"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">Número de lote</Label>
                        <Input
                          value={batchNumber}
                          onChange={(e) => setBatchNumber(e.target.value)}
                          className="sm:col-span-3"
                          placeholder="Ej: LOTE-2024-001"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                        <Label className="sm:text-right pt-2">
                          Fecha compra
                        </Label>
                        <div className="sm:col-span-3">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !purchaseDate && "text-muted-foreground",
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {purchaseDate
                                  ? format(purchaseDate, "PPP", { locale: es })
                                  : "Seleccionar fecha"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={purchaseDate}
                                onSelect={setPurchaseDate}
                                initialFocus
                                locale={es}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                        <Label className="sm:text-right pt-2">
                          Fecha caducidad
                        </Label>
                        <div className="sm:col-span-3">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !expiryDate && "text-muted-foreground",
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {expiryDate
                                  ? format(expiryDate, "PPP", { locale: es })
                                  : "Seleccionar fecha"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={expiryDate}
                                onSelect={setExpiryDate}
                                initialFocus
                                locale={es}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">
                          Cant. mínima pedido
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={minimumOrderQuantity}
                          onChange={(e) =>
                            setMinimumOrderQuantity(e.target.value)
                          }
                          className="sm:col-span-3"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">
                          Tiempo entrega (días)
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={leadTimeDays}
                          onChange={(e) => setLeadTimeDays(e.target.value)}
                          className="sm:col-span-3"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">
                          Ventas diarias promedio
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={dailySalesRate}
                          onChange={(e) => setDailySalesRate(e.target.value)}
                          className="sm:col-span-3"
                          placeholder="Unidades vendidas por día"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                        <Label className="sm:text-right">
                          Punto de reorden
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={reorderPoint}
                          onChange={(e) => setReorderPoint(e.target.value)}
                          className="sm:col-span-3"
                          placeholder="Stock mínimo para reordenar"
                        />
                      </div>

                      {/* Calculadora de punto de reorden */}
                      {dailySalesRate && leadTimeDays && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-blue-800 mb-1">
                            💡 Calculadora de punto de reorden
                          </p>
                          <p className="text-xs text-blue-600">
                            Ventas diarias: {dailySalesRate} × Tiempo entrega:{" "}
                            {leadTimeDays} días =
                            <span className="font-semibold">
                              {" "}
                              {parseFloat(dailySalesRate) *
                                parseInt(leadTimeDays)}{" "}
                              unidades
                            </span>
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            Sugerencia: Establece el punto de reorden en{" "}
                            {(
                              parseFloat(dailySalesRate) *
                              parseInt(leadTimeDays)
                            ).toFixed(0)}{" "}
                            unidades
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between pt-4 border-t gap-4">
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
                className="gap-2 order-2 sm:order-1"
              >
                <Trash2 className="h-4 w-4" />
                Eliminar Producto
              </Button>

              <div className="flex gap-2 order-1 sm:order-2">
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  data-testid="button-save-inventory"
                  className="flex-1"
                >
                  {updateMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Guardar Cambios"
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export default function ShopInventory() {
  const currentShop = getCurrentShop();
  const currentUser = getCurrentUser();
  const userRole = currentUser?.role;

  // ============ VERIFICAR SI ES OWNER EN MODO TIENDA ============
  const isOwnerMode = isOwnerInShopMode();

  // Log para depuración
  console.log("👑 ShopInventory - ¿Es owner en modo tienda?", isOwnerMode);

  // ============ VERIFICAR ACCESO: OWNER TIENE ACCESO TOTAL ============
  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-8 mt-20">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso restringido</h3>
            <p className="text-muted-foreground">
              No tienes permiso para gestionar inventario.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si es owner, tiene acceso total aunque no tenga rol de admin/manager/cashier
  const hasInventoryAccess =
    isOwnerMode ||
    currentUser.role === "supervisor" ||
    currentUser.role === "store_manager" ||
    currentUser.role === "cashier";

  if (!hasInventoryAccess) {
    return (
      <div className="container mx-auto px-4 py-8 mt-20">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso restringido</h3>
            <p className="text-muted-foreground">
              No tienes permiso para gestionar inventario.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showAddProduct, setShowAddProduct] = useState(false);

  // {NUEVO: Estado para filtro por stock}
  const [stockFilter, setStockFilter] = useState<string>("");
  const [stockFilterOperator, setStockFilterOperator] = useState<
    "eq" | "lt" | "lte" | "gt" | "gte"
  >("eq");

  // {esta sección reinicia la paginación cuando cambian los filtros de stock}
  useEffect(() => {
    setCurrentPage(1);
  }, [stockFilter, stockFilterOperator]);

  // ============ ESTADO PARA MODAL DE PROVEEDOR GLOBAL ============
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierModalMode, setSupplierModalMode] = useState<"create" | "edit">(
    "create",
  );
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);

  // Estados de paginación para inventario
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ============ DETECTAR SI EL SERVIDOR ESTÁ ACCESIBLE ============
  const [isServerReachable, setIsServerReachable] = useState<boolean | null>(
    null,
  );

  useEffect(() => {
    const checkServer = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, 2000);

        const response = await fetch("/api/health", {
          method: "HEAD",
          signal: controller.signal,
          cache: "no-cache",
        }).catch((error) => {
          return null;
        });

        clearTimeout(timeoutId);
        const isReachable = response !== null && response.ok;
        setIsServerReachable(isReachable);
      } catch (error) {
        setIsServerReachable(false);
      }
    };

    checkServer();

    // Verificar cada 30 segundos si el servidor vuelve
    const interval = setInterval(checkServer, 30000);
    return () => clearInterval(interval);
  }, []);

  // ============ ESTADO OFFLINE COMBINADO ============
  const isBrowserOffline = !navigator.onLine;
  const isOfflineMode = isBrowserOffline || isServerReachable === false;

  // ============ Obtener información del plan ============
  const { can } = usePlan();

  // ============ EFECTO PARA ESCUCHAR EVENTOS DE PROVEEDOR GLOBALMENTE ============
  useEffect(() => {
    const handleOpenSupplierModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      // Verificar que sea para esta tienda
      if (
        !customEvent.detail ||
        customEvent.detail.shopId === currentShop?.id
      ) {
        setSelectedSupplier(null);
        setSupplierModalMode("create");
        setSupplierModalOpen(true);
      }
    };

    const handleOpenSupplierEditModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      // Verificar que sea para esta tienda
      if (
        !customEvent.detail ||
        customEvent.detail.shopId === currentShop?.id
      ) {
        setSelectedSupplier(customEvent.detail.supplier);
        setSupplierModalMode("edit");
        setSupplierModalOpen(true);
      }
    };

    // Escuchar eventos personalizados
    window.addEventListener("open-supplier-modal", handleOpenSupplierModal);
    window.addEventListener(
      "open-supplier-edit-modal",
      handleOpenSupplierEditModal,
    );

    // Limpiar eventos al desmontar
    return () => {
      window.removeEventListener(
        "open-supplier-modal",
        handleOpenSupplierModal,
      );
      window.removeEventListener(
        "open-supplier-edit-modal",
        handleOpenSupplierEditModal,
      );
    };
  }, [currentShop?.id]);

  // ============ FUNCIÓN PARA CARGAR INVENTARIO ONLINE ============
  // {MODIFICADO: Ahora incluye filtro por stock}
  const fetchInventoryOnline = useCallback(async () => {
    console.log("🟢 [DEBUG] fetchInventoryOnline ejecutado");

    const filters: any = {
      page: currentPage,
      limit: itemsPerPage,
      search: search,
    };

    if (stockFilter && stockFilter.trim() !== "") {
      const stockValue = parseInt(stockFilter, 10);
      if (!isNaN(stockValue)) {
        filters.stockFilter = stockValue;
        filters.stockOperator = stockFilterOperator;
      }
    }

    console.log("🟢 [DEBUG] Filtros enviados:", filters);

    const result = await getShopInventoryPaginated(currentShop!.id, filters);
    console.log("🟢 [DEBUG] Resultado recibido:", result.data?.length, "items");

    return result.data || [];
  }, [
    currentPage,
    itemsPerPage,
    search,
    stockFilter,
    stockFilterOperator,
    currentShop?.id,
  ]);

  // ============ REFERENCIA PARA RECARGAR INVENTARIO ============
  const [inventoryRefetch, setInventoryRefetch] = useState<(() => void) | null>(
    null,
  );

  // ============ DATOS OFFLINE PARA INVENTARIO ============
  const {
    data: inventory,
    loading: inventoryLoading,
    error: inventoryError,
    saveOffline: saveInventoryOffline,
    refetch: refetchInventory,
  } = useOfflineInventory({
    shopId: currentShop?.id || "",
    fetchOnlineData: fetchInventoryOnline,
    isOffline: isOfflineMode,
  });
  console.log(
    "🔵 [DEBUG] useOfflineInventory - inventory:",
    inventory?.length,
    "items",
  );

  // ============ LOG PARA VER CUANDO CAMBIA EL INVENTARIO ============
  useEffect(() => {}, [inventory]);

  // ============ GUARDAR LA FUNCIÓN REFETCH ============
  useEffect(() => {
    if (refetchInventory) {
      setInventoryRefetch(() => refetchInventory);
    }
  }, [refetchInventory]);

  // ============ FUNCIÓN PARA CARGAR PRODUCTOS ONLINE ============
  const fetchProductsOnline = useCallback(async () => {
    return await getProducts();
  }, []);

  // ============ DATOS OFFLINE PARA PRODUCTOS ============
  const {
    data: products,
    loading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useOfflineProducts({
    fetchOnlineData: fetchProductsOnline,
    isOffline: isOfflineMode,
  });

  // ============ FUNCIÓN PARA VERIFICAR SI EL SERVIDOR ESTÁ ACCESIBLE ============
  const checkServerReachable = async (): Promise<boolean> => {
    try {
      // Intentar hacer una petición ligera al servidor
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 2000); // Timeout de 2 segundos

      const response = await fetch("/api/health", {
        method: "HEAD", // Usamos HEAD para no recibir el body
        signal: controller.signal,
        cache: "no-cache", // Evitar caché
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      }).catch((error) => {
        return null;
      });

      clearTimeout(timeoutId);

      const isReachable = response !== null && response.ok;

      if (response) {
      }

      return isReachable;
    } catch (error) {
      if (error instanceof Error) {
        console.log("❌ Nombre del error:", error.name);
        console.log("❌ Mensaje:", error.message);
        console.log("❌ Stack:", error.stack);
      }
      return false;
    }
  };

  const createProductMutation = useMutation({
    mutationFn: async (productData: {
      name: string;
      category: string;
      price: string;
      stock: number;
      threshold: number;
      supplierId?: string;
      supplierNotes?: string;
      acquisitionCost?: string;
      profitMargin?: string;
      batchNumber?: string;
      purchaseDate?: string;
      expiryDate?: string;
      minimumOrderQuantity?: number;
      leadTimeDays?: number;
      dailySalesRate?: string;
      reorderPoint?: number;
      priceSuggestion?: string;
      marginSuggestion?: string;
    }) => {
      // ============ DETECCIÓN DE OFFLINE ============
      const isBrowserOffline = !navigator.onLine;
      let isServerReachable = false;

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1000);
        const response = await fetch("/api/health", {
          method: "HEAD",
          signal: controller.signal,
          cache: "no-cache",
        }).catch(() => null);
        clearTimeout(timeoutId);
        isServerReachable = response !== null && response.ok;
      } catch (error) {
        isServerReachable = false;
      }

      const isOffline = isBrowserOffline || !isServerReachable;

      if (isOffline) {
        // ============ MODO OFFLINE ============
        const tempProductId = `temp_product_${Date.now()}`;
        const tempInventoryId = `temp_inventory_${Date.now()}`;

        const tempProduct = {
          id: tempProductId,
          name: productData.name,
          category: productData.category,
          lastUpdated: new Date().toISOString(),
        };

        await indexedDBService.saveProducts([tempProduct]);

        const tempInventoryItem = {
          id: tempInventoryId,
          shopId: currentShop!.id,
          productId: tempProductId,
          price: productData.price,
          stock: productData.stock,
          lowStockThreshold: productData.threshold,
          acquisitionCost: productData.acquisitionCost || undefined,
          profitMargin: productData.profitMargin || undefined,
          supplierId: productData.supplierId || null,
          supplierNotes: productData.supplierNotes || undefined,
          batchNumber: productData.batchNumber || undefined,
          purchaseDate: productData.purchaseDate,
          expiryDate: productData.expiryDate,
          minimumOrderQuantity: productData.minimumOrderQuantity || undefined,
          leadTimeDays: productData.leadTimeDays || undefined,
          dailySalesRate: productData.dailySalesRate || undefined,
          reorderPoint: productData.reorderPoint || undefined,
          lastUpdated: new Date().toISOString(),
        };

        await indexedDBService.saveInventoryItem(tempInventoryItem);
        window.dispatchEvent(new CustomEvent("inventory-changed"));
        window.dispatchEvent(new CustomEvent("products-changed"));

        const pendingOp = {
          id: `op_${Date.now()}`,
          type: "create" as const,
          entityType: "inventory" as const,
          data: {
            product: tempProduct,
            inventory: tempInventoryItem,
            productData: {
              name: productData.name,
              category: productData.category,
              price: productData.price,
              stock: productData.stock,
              threshold: productData.threshold,
              supplierId: productData.supplierId,
              supplierNotes: productData.supplierNotes,
              acquisitionCost: productData.acquisitionCost,
              profitMargin: productData.profitMargin,
              batchNumber: productData.batchNumber,
              purchaseDate: productData.purchaseDate,
              expiryDate: productData.expiryDate,
              minimumOrderQuantity: productData.minimumOrderQuantity,
              leadTimeDays: productData.leadTimeDays,
              dailySalesRate: productData.dailySalesRate,
              reorderPoint: productData.reorderPoint,
            },
          },
          timestamp: new Date().toISOString(),
          retryCount: 0,
          priority: "normal" as const,
          originalCreatedAt: new Date().toISOString(),
          status: "pending" as const,
        };

        await indexedDBService.addPendingOperation(pendingOp);
        window.dispatchEvent(new CustomEvent("pending-changed"));

        return { success: true, offline: true };
      } else {
        // ============ MODO ONLINE ============
        try {
          const result = await createProduct(currentShop!.id, {
            name: productData.name,
            category: productData.category,
            price: productData.price,
            stock: productData.stock,
            threshold: productData.threshold,
            supplierId: productData.supplierId,
            supplierNotes: productData.supplierNotes,
            acquisitionCost: productData.acquisitionCost,
            profitMargin: productData.profitMargin,
            batchNumber: productData.batchNumber,
            purchaseDate: productData.purchaseDate,
            expiryDate: productData.expiryDate,
            minimumOrderQuantity: productData.minimumOrderQuantity,
            leadTimeDays: productData.leadTimeDays,
            dailySalesRate: productData.dailySalesRate,
            reorderPoint: productData.reorderPoint,
          });
          return { success: true, offline: false };
        } catch (error: any) {
          // Capturar el error específico de límite de productos
          if (error.status === 403) {
            setTimeout(() => {
              toast({
                title: "⚠️ Límite de productos alcanzado",
                description:
                  error.message ||
                  "Has alcanzado el límite de productos para tu plan actual.",
                variant: "destructive",
                duration: 8000,
                action: (
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (window.location.href = "/owner/plans")}
                      className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                    >
                      Mejorar Plan
                    </Button>
                  </div>
                ),
              });
            }, 100);
            throw error;
          }
          throw error;
        }
      }
    },
    onSuccess: (result, variables) => {
      console.log(
        "🔴🔴🔴 [DEBUG] onSuccess de createProductMutation ejecutado",
      );
      console.log("🔴 [DEBUG] result:", result);
      console.log("🔴 [DEBUG] currentShop?.id:", currentShop?.id);

      if (result?.offline) {
        toast({
          title: "📴 Producto guardado localmente",
          description: "Se sincronizará cuando tengas conexión",
          duration: 5000,
        });
      } else {
        toast({
          title: "✅ Producto creado",
          description: "El producto se agregó al inventario correctamente.",
        });
      }

      // {CRÍTICO: Disparar eventos para TODOS los casos (online y offline)}
      window.dispatchEvent(new CustomEvent("inventory-changed"));
      window.dispatchEvent(new CustomEvent("products-changed"));

      // {Invalidar queries de React Query}
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({
        queryKey: ["shopInventory", currentShop?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["inventoryCalculations", currentShop?.id],
      });

      // {NUEVO: Forzar refetch inmediato}
      setTimeout(() => {
        console.log("🔴 [DEBUG] Ejecutando refetch inmediato...");

        // {Refetch de React Query}
        queryClient.refetchQueries({ queryKey: ["products"] });
        queryClient.refetchQueries({
          queryKey: ["shopInventory", currentShop?.id],
        });
        queryClient.refetchQueries({
          queryKey: ["inventoryCalculations", currentShop?.id],
        });

        // {CRÍTICO: Llamar directamente a las funciones refetch de los hooks}
        if (inventoryRefetch) {
          console.log("🔴 [DEBUG] Llamando a inventoryRefetch() directamente");
          inventoryRefetch();
        }
        if (refetchProducts) {
          console.log("🔴 [DEBUG] Llamando a refetchProducts() directamente");
          refetchProducts();
        }

        console.log(
          "🔄 Refetch automático ejecutado después de crear producto",
        );
      }, 100);
    },
    onError: (error: any) => {
      console.error("❌ Error en mutación padre:", error);
      if (error.status !== 403) {
        toast({
          title: "Error",
          description: error.message || "No se pudo crear el producto",
          variant: "destructive",
        });
      }
    },
  });

  // ============ FUNCIÓN PARA ACTUALIZAR INVENTARIO ============
  const refreshInventory = useCallback(() => {
    console.log("🔄 Actualizando todas las pestañas del inventario...");

    // Mostrar toast de carga
    toast({
      title: "Actualizando",
      description: "Recargando datos del inventario...",
    });

    // ============ PASO 1: Invalidar TODAS las queries relacionadas ============

    // Inventario principal
    queryClient.invalidateQueries({
      queryKey: ["shopInventory", currentShop?.id],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "shopInventory",
        currentShop?.id,
        currentPage,
        itemsPerPage,
        search,
      ],
    });

    // Productos
    queryClient.invalidateQueries({ queryKey: ["products"] });

    // Cálculos y análisis (pestaña Análisis)
    queryClient.invalidateQueries({
      queryKey: ["inventoryCalculations", currentShop?.id],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "inventoryCalculations",
        currentShop?.id,
        currentPage,
        itemsPerPage,
        search,
      ],
    });

    // Proveedores (pestaña Proveedores)
    queryClient.invalidateQueries({
      queryKey: ["shopSuppliers", currentShop?.id],
    });

    // Reportes (pestaña Reportes)
    queryClient.invalidateQueries({
      queryKey: ["inventoryReport", currentShop?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ["inventoryValueReport", currentShop?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ["stockMovementReport", currentShop?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ["expiringProductsReport", currentShop?.id],
    });

    // ============ PASO 2: Usar refetch si están disponibles ============

    // Recargar inventario
    if (inventoryRefetch) {
      setTimeout(() => {
        inventoryRefetch();
        console.log("🔄 Refetch de inventario ejecutado");
      }, 50);
    }

    // Recargar productos
    if (refetchProducts) {
      setTimeout(() => {
        refetchProducts();
        console.log("🔄 Refetch de productos ejecutado");
      }, 50);
    }

    // ============ PASO 3: Forzar recarga de todas las queries ============
    setTimeout(() => {
      // Recargar cálculos de inventario
      queryClient.refetchQueries({
        queryKey: ["inventoryCalculations", currentShop?.id],
      });

      // Recargar proveedores
      queryClient.refetchQueries({
        queryKey: ["shopSuppliers", currentShop?.id],
      });

      // Recargar reportes
      queryClient.refetchQueries({
        queryKey: ["inventoryReport", currentShop?.id],
      });
      queryClient.refetchQueries({
        queryKey: ["inventoryValueReport", currentShop?.id],
      });
      queryClient.refetchQueries({
        queryKey: ["stockMovementReport", currentShop?.id],
      });
      queryClient.refetchQueries({
        queryKey: ["expiringProductsReport", currentShop?.id],
      });

      console.log("✅ Todas las pestañas actualizadas");

      toast({
        title: "✅ Datos actualizados",
        description:
          "Inventario, análisis, proveedores y reportes actualizados.",
        duration: 3000,
      });
    }, 100);

    console.log("✅ Proceso de actualización completado");
  }, [
    currentShop?.id,
    inventoryRefetch,
    refetchProducts,
    queryClient,
    currentPage,
    itemsPerPage,
    search,
  ]);

  const handleExportExcel = async () => {
    try {
      toast({
        title: "Generando Excel...",
        description: "Obteniendo todos los productos...",
      });

      const allItems = await getShopInventory(currentShop!.id);

      const formatted = allItems.map((item: any, index: number) => {
        const product = products.find((p: any) => p.id === item.productId);
        const cost = parseFloat(item.acquisitionCost || "0");
        const price = parseFloat(item.price || "0");
        const margin = cost > 0 ? ((price - cost) / cost * 100).toFixed(1) : "—";

        return {
          "#": index + 1,
          "Producto": product?.name || "—",
          "Categoría": product?.category || "—",
          "Precio": Number(price).toFixed(2),
          "Costo": Number(cost).toFixed(2),
          "Margen %": margin,
          "Proveedor": item.supplierName || "—",
          "Stock": Number(item.stock),
          "Stock Mínimo": item.lowStockThreshold ?? 0,
          "Lote": item.batchNumber || "—",
          "Caduca": item.expiryDate
            ? new Date(item.expiryDate).toLocaleDateString("es-CU")
            : "—",
        };
      });

      await exportToExcel(
        formatted,
        `inventario_${new Date().toISOString().split("T")[0]}`,
        "Inventario",
      );

      toast({
        title: "✅ Exportación exitosa",
        description: `${formatted.length} productos exportados a Excel`,
      });
    } catch (error: any) {
      toast({
        title: "❌ Error al exportar",
        description: error.message || "Error desconocido",
        variant: "destructive",
      });
    }
  };

  // ============ EFECTO PARA DETECTAR CAMBIOS EN LA CONEXIÓN ============
  useEffect(() => {
    const handleOnline = () => {
      console.log("📡 Conexión restaurada - online");
      // Opcional: Refrescar datos cuando se recupera la conexión
      queryClient.invalidateQueries({
        queryKey: ["shopInventory", currentShop?.id],
      });
    };

    const handleOffline = () => {
      console.log("📡 Conexión perdida - offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [currentShop?.id, queryClient]);

  // ============ EFECTO PARA RECARGAR CUANDO CAMBIA EL INVENTARIO ============
  // {NUEVO: Escuchar eventos de cambio de inventario para actualizar automáticamente}
  useEffect(() => {
    const handleInventoryChanged = () => {
      console.log("🔄 Evento 'inventory-changed' detectado, actualizando...");

      // {PASO 1: Invalidar todas las queries}
      queryClient.invalidateQueries({
        queryKey: ["shopInventory", currentShop?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["inventoryCalculations", currentShop?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });

      // {PASO 2: Forzar refetch inmediato}
      setTimeout(() => {
        console.log("🔄 Ejecutando refetch inmediato...");

        // {Refetch de React Query}
        queryClient.refetchQueries({
          queryKey: ["shopInventory", currentShop?.id],
        });
        queryClient.refetchQueries({
          queryKey: ["inventoryCalculations", currentShop?.id],
        });
        queryClient.refetchQueries({
          queryKey: ["products"],
        });

        // {CRÍTICO: Llamar directamente a inventoryRefetch para forzar actualización}
        if (inventoryRefetch) {
          console.log("🔄 Llamando a inventoryRefetch() directamente");
          inventoryRefetch();
        }

        // {NUEVO: También forzar refetch de productos}
        if (refetchProducts) {
          console.log("🔄 Llamando a refetchProducts() directamente");
          refetchProducts();
        }
      }, 50);
    };

    const handleProductsChanged = () => {
      console.log("🔄 Evento 'products-changed' detectado, actualizando...");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ["products"] });
        if (refetchProducts) {
          refetchProducts();
        }
      }, 50);
    };

    window.addEventListener("inventory-changed", handleInventoryChanged);
    window.addEventListener("products-changed", handleProductsChanged);

    return () => {
      window.removeEventListener("inventory-changed", handleInventoryChanged);
      window.removeEventListener("products-changed", handleProductsChanged);
    };
  }, [currentShop?.id, queryClient, inventoryRefetch, refetchProducts]);

  // ============ CÁLCULOS (solo online) ============
  const { data: calculationsData, isLoading: calculationsLoading } = useQuery({
    queryKey: [
      "inventoryCalculations",
      currentShop?.id,
      currentPage,
      itemsPerPage,
      search,
    ],
    queryFn: () =>
      getInventoryCalculations(currentShop!.id, {
        page: currentPage,
        limit: itemsPerPage,
        search: search,
      }),
    enabled: !!currentShop && !inventoryLoading && !isOfflineMode, // No ejecutar si estamos offline
  });

  // ============ OBTENER PROVEEDORES ============
  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ["shopSuppliers", currentShop?.id],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/shops/${currentShop?.id}/suppliers`);
        console.log("📡 Status de respuesta:", response.status);

        if (!response.ok) {
          console.error(
            "❌ Error en respuesta:",
            response.status,
            response.statusText,
          );
          return [];
        }

        const data = await response.json();

        return data.suppliers || [];
      } catch (error) {
        return [];
      }
    },
    enabled: !!currentShop?.id,
  });

  // Log para ver suppliers después de cargar
  useEffect(() => {
    if (suppliers.length > 0) {
    }
  }, [suppliers]);

  // ============ FUNCIÓN PARA OBTENER NOMBRE DE PROVEEDOR ============
  const getSupplierName = (supplierId: string | null | undefined): string => {
    // Si no hay ID, retornar guión
    if (!supplierId) {
      return "—";
    }

    // Verificar que suppliers existe y es un array
    if (!suppliers || !Array.isArray(suppliers)) {
      return `Proveedor ${supplierId.substring(0, 8)}...`;
    }

    // Si el array de suppliers está vacío
    if (suppliers.length === 0) {
      return `Proveedor ${supplierId.substring(0, 8)}...`;
    }

    // Buscar el proveedor en la lista
    const supplier = suppliers.find((s: Supplier) => {
      const match = s.id === supplierId;
      return match;
    });

    // Si se encuentra, retornar el nombre
    if (supplier) {
      return supplier.name;
    }

    // Si no se encuentra, retornar el ID truncado
    return `Proveedor ${supplierId.substring(0, 8)}...`;
  };

  // Extraer datos
  const calculations = calculationsData?.calculations || {};
  const inventorySummary = calculationsData?.summary || {};

  // Calcular total de páginas (ajustado para offline)
  const inventoryTotal = totalItems || inventory?.length || 0;
  const filteredInventory = inventory || [];

  // ============ CALCULAR TOTAL DE PÁGINAS ============
  useEffect(() => {
    if (!isOfflineMode && calculationsData?.summary?.totalItems) {
      // Modo online: usar datos del backend
      const total = calculationsData.summary.totalItems;
      setTotalItems(total);
      setTotalPages(Math.ceil(total / itemsPerPage));
    } else {
      // Modo offline: calcular basado en datos locales
      setTotalItems(inventory?.length || 0);
      setTotalPages(Math.ceil((inventory?.length || 0) / itemsPerPage));
    }
  }, [calculationsData, inventory, itemsPerPage, isOfflineMode]);

  const isLoading = inventoryLoading || productsLoading || calculationsLoading;

  if (!currentShop) return null;

  // ============ MUTACIÓN PARA CREAR PRODUCTO (SOPORTE OFFLINE) ============

  return (
    <PageContainer className="container mx-auto px-4 py-8">
      <div className="mb-8 space-y-4">
        <h1 className="text-3xl font-heading font-bold uppercase text-center sm:text-left">
          Catálogo de Productos
        </h1>

        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-end gap-2">
          {/* ============ BOTÓN DE SINCRONIZACIÓN ============ */}
          <SyncButton
            shopId={currentShop?.id || ""}
            onSyncComplete={() => {
              // Recargar datos después de sincronizar
              queryClient.invalidateQueries({ queryKey: ["products"] });
              queryClient.invalidateQueries({
                queryKey: ["shopInventory", currentShop?.id],
              });
            }}
          />

          {/* ============ BOTÓN DE ACTUALIZAR ============ */}
          <Button
            variant="outline"
            onClick={refreshInventory}
            disabled={isLoading}
            className="w-full sm:w-auto"
            title="Actualizar inventario"
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Actualizar</span>
            <span className="sm:hidden">↻</span>
          </Button>

          {/* Botón Agregar Producto */}
          <Button
            variant="default"
            onClick={() => setShowAddProduct(true)}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Agregar producto
          </Button>

          {/* ============ Botón Agregar Proveedor - Solo admin/manager/owner Y planes avanzados ============ */}
          {(userRole === "supervisor" || userRole === "store_manager" || isOwnerMode) &&
          can.useAdvancedInventory() ? (
            <Button
              variant="default"
              onClick={() => {
                setSelectedSupplier(null);
                setSupplierModalMode("create");
                setSupplierModalOpen(true);
              }}
              className="w-full sm:w-auto"
            >
              <Truck className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Agregar Proveedor</span>
              <span className="sm:hidden">Agregar Proveedor</span>
            </Button>
          ) : (userRole === "supervisor" || userRole === "store_manager" || isOwnerMode) &&
            !can.useAdvancedInventory() ? (
            <div className="relative group w-full sm:w-auto">
              <Button
                variant="default"
                className="w-full sm:w-auto opacity-50 cursor-not-allowed"
                disabled
              >
                <Truck className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Agregar Proveedor</span>
                <span className="sm:hidden">Agregar Proveedor</span>
              </Button>
              <div className="absolute right-0 mt-2 w-64 p-2 bg-amber-100 border border-amber-300 rounded-md text-xs text-amber-800 shadow-lg z-50 hidden group-hover:block">
                <p className="font-medium">🔒 Bloqueado por plan</p>
                <p>
                  La gestión de proveedores requiere plan Profesional o
                  Enterprise.
                </p>
                <p className="mt-1 text-amber-700">
                  <Button
                    variant="link"
                    className="text-xs text-amber-900 underline p-0 h-auto"
                    onClick={() => (window.location.href = "/owner/plans")}
                  >
                    Mejorar plan
                  </Button>
                </p>
              </div>
            </div>
          ) : null}

          {/* Botón Exportar a Excel */}
          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={isLoading}
            className="w-full sm:w-auto"
            title="Exportar todo el inventario a Excel"
          >
            <FileText className="mr-2 h-4 w-4 text-green-600" />
            <span className="hidden sm:inline">Exportar Excel</span>
            <span className="sm:hidden">Excel</span>
          </Button>

          {/* Botón de información de columnas (opcional) */}
          <div className="relative group">
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              title="Información de columnas"
            >
              <span className="hidden sm:inline">Columnas</span>
              <span className="sm:hidden">ℹ️</span>
            </Button>
            <div className="absolute right-0 mt-2 w-64 p-3 bg-white border rounded-lg shadow-lg z-50 hidden group-hover:block">
              <p className="text-sm font-medium mb-2">
                Información de columnas:
              </p>
              <ul className="text-xs space-y-1 text-muted-foreground">
                <li>
                  📱 <strong>Móvil</strong>: Producto, Precio, Stock, Acciones
                </li>
                <li>
                  💻 <strong>Tablet</strong>: +Categoría, Alerta
                </li>
                <li>
                  🖥️ <strong>Desktop</strong>: +Costo, Margen %
                </li>
                <li>
                  🖥️ <strong>XL</strong>: +Proveedor, Lote, Caducidad
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="space-y-4">
            {/* {Fila 1: Título y buscador principal} */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-xl">Productos en Inventario</CardTitle>
              <div className="w-full sm:w-auto">
                <Input
                  placeholder="Buscar producto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="input-search-inventory"
                />
              </div>
            </div>

            {/* {NUEVO: Fila 2 - Filtro por stock} */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  Filtrar por stock:
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* {Selector de operador} */}
                <Select
                  value={stockFilterOperator}
                  onValueChange={(v: any) => setStockFilterOperator(v)}
                >
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eq">Igual a (=)</SelectItem>
                    <SelectItem value="lt">Menor que (&lt;)</SelectItem>
                    <SelectItem value="lte">Menor o igual (≤)</SelectItem>
                    <SelectItem value="gt">Mayor que (&gt;)</SelectItem>
                    <SelectItem value="gte">Mayor o igual (≥)</SelectItem>
                  </SelectContent>
                </Select>

                {/* {Input para el valor} */}
                <Input
                  type="number"
                  placeholder="Cantidad"
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  className="w-[120px] h-9"
                  min="0"
                  data-testid="input-filter-stock"
                />

                {/* {Botones de filtros rápidos} */}
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    onClick={() => {
                      setStockFilter("0");
                      setStockFilterOperator("eq");
                      setCurrentPage(1);
                    }}
                  >
                    Sin stock
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    onClick={() => {
                      setStockFilter("10");
                      setStockFilterOperator("lte");
                      setCurrentPage(1);
                    }}
                  >
                    Stock bajo
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    onClick={() => {
                      setStockFilter("50");
                      setStockFilterOperator("gt");
                      setCurrentPage(1);
                    }}
                  >
                    Stock alto
                  </Button>
                </div>

                {/* {Botón para limpiar filtro} */}
                {stockFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-muted-foreground"
                    onClick={() => {
                      setStockFilter("");
                      setCurrentPage(1);
                    }}
                  >
                    Limpiar filtro
                  </Button>
                )}
              </div>

              {/* {Indicador de filtro activo} */}
              {stockFilter && (
                <Badge variant="secondary" className="ml-auto">
                  Filtro activo: Stock{" "}
                  {stockFilterOperator === "eq"
                    ? "="
                    : stockFilterOperator === "lt"
                      ? "<"
                      : stockFilterOperator === "lte"
                        ? "≤"
                        : stockFilterOperator === "gt"
                          ? ">"
                          : "≥"}{" "}
                  {stockFilter}
                </Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {isLoading
              ? "Cargando..."
              : isOfflineMode
                ? `📴 ${inventoryTotal} productos disponibles (modo offline)`
                : `${inventorySummary.totalItems || inventoryTotal} productos encontrados`}
            {!isOfflineMode &&
              inventory.length <
                (inventorySummary.totalItems || inventoryTotal) &&
              ` (${inventory.length} en esta página)`}
            {/* {NUEVO: Mostrar filtro aplicado} */}
            {stockFilter && !isOfflineMode && (
              <span className="ml-2 text-blue-600">
                • Filtro: Stock{" "}
                {stockFilterOperator === "eq"
                  ? "="
                  : stockFilterOperator === "lt"
                    ? "<"
                    : stockFilterOperator === "lte"
                      ? "≤"
                      : stockFilterOperator === "gt"
                        ? ">"
                        : "≥"}{" "}
                {stockFilter}
              </span>
            )}
            {!isOfflineMode &&
              inventorySummary.averageMargin > 0 &&
              ` • Margen promedio: ${inventorySummary.averageMargin.toFixed(1)}%`}
            {!isOfflineMode &&
              inventorySummary.lowStockCount > 0 &&
              ` • ${inventorySummary.lowStockCount} con stock bajo`}
          </p>
        </CardHeader>
        <CardContent>
          {/* === SECCIÓN ÚNICA: CATÁLOGO DE PRODUCTOS === */}
          {/* ✅ Se eliminó el contenedor <Tabs> y sus pestañas (analysis, suppliers, reports)
    porque esas secciones ahora serán páginas independientes.
    Esto corrige el error de sintaxis por ausencia de <TabsList>/<TabsTrigger> */}
          <div className="space-y-4">
            {/* ============ INDICADOR DE MODO OFFLINE ============ */}
            {isOfflineMode && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                <WifiOff className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Modo sin conexión
                  </p>
                  <p className="text-xs text-amber-700">
                    Mostrando datos guardados localmente. Los cambios se
                    sincronizarán cuando vuelva la conexión.
                  </p>
                </div>
              </div>
            )}
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : filteredInventory.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  No hay productos en el inventario
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowAddProduct(true)}
                  className="mt-4"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar primer producto
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px] text-center">#</TableHead>
                      <TableHead className="min-w-[150px]">Producto</TableHead>
                      <TableHead className="min-w-[120px] hidden md:table-cell">
                        Categoría
                      </TableHead>
                      <TableHead className="min-w-[100px]">
                        Precio ({getCurrencySymbol()})
                      </TableHead>
                      <TableHead className="min-w-[90px] hidden lg:table-cell">
                        Costo ({getCurrencySymbol()})
                      </TableHead>
                      <TableHead className="min-w-[80px] hidden lg:table-cell">
                        Margen %
                      </TableHead>
                      <TableHead className="min-w-[120px] hidden xl:table-cell">
                        Proveedor
                      </TableHead>
                      <TableHead className="min-w-[80px]">Stock</TableHead>
                      <TableHead className="min-w-[90px] hidden md:table-cell">
                        Alerta
                      </TableHead>
                      <TableHead className="min-w-[70px] hidden xl:table-cell">
                        Lote
                      </TableHead>
                      <TableHead className="min-w-[100px] hidden xl:table-cell">
                        Caduca
                      </TableHead>
                      <TableHead className="min-w-[80px] text-right">
                        Acciones
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInventory.map((item, index) => {
                      const product = products.find(
                        (p) => p.id === item.productId,
                      )!;
                      const isTemp = item.id.startsWith("temp_");
                       const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;
                      const itemCalculations = calculations[item.id] || {
                        marginPercentage: 0,
                        marginCategory: "none",
                        daysUntilExpiry: null,
                        expiryStatus: "none",
                        suggestedReorderPoint: 0,
                        cssClasses: { margin: "", expiry: "", stock: "" },
                      };
                      const cost = parseFloat(item.acquisitionCost || "0");
                      const price = parseFloat(item.price || "0");
                      return (
                        <TableRow
                          key={item.id}
                          data-testid={`row-inventory-${item.id}`}
                          className={
                            isTemp
                              ? "bg-amber-50/50 border-l-4 border-l-amber-500"
                              : ""
                          }
                        >
                          <TableCell className="text-center font-mono text-sm text-muted-foreground">
                            {rowNumber}
                          </TableCell>
                          <TableCell
                            className="font-medium truncate max-w-[150px]"
                            title={product?.name || "—"}
                          >
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="truncate">
                                  {product?.name || "—"}
                                </span>
                                {isTemp && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs bg-amber-50 text-amber-700 border-amber-200"
                                  >
                                    Pendiente
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground md:hidden">
                                {product?.category || "—"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell
                            className="truncate max-w-[120px] hidden md:table-cell"
                            title={product?.category || "—"}
                          >
                            {product?.category || "—"}
                          </TableCell>
                          <TableCell
                            data-testid={`text-price-${item.id}`}
                            className="whitespace-nowrap"
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {getCurrencySymbol()}
                                {item.price}
                              </span>
                              {cost > 0 && (
                                <span className="text-xs text-muted-foreground lg:hidden">
                                  Costo: {getCurrencySymbol()}
                                  {cost.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap hidden lg:table-cell">
                            {cost > 0 ? (
                              <span
                                className={
                                  cost > price
                                    ? "text-destructive"
                                    : "text-green-600"
                                }
                              >
                                {getCurrencySymbol()}
                                {cost.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {cost > 0 ? (
                              <div
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${itemCalculations.cssClasses.margin}`}
                              >
                                {itemCalculations.marginPercentage.toFixed(1)}%
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="truncate max-w-[120px] hidden xl:table-cell">
                            {item.supplierId ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-xs">
                                  {getSupplierName(item.supplierId)}
                                </span>
                                {item.supplierNotes && (
                                  <span
                                    className="text-xs text-muted-foreground truncate"
                                    title={item.supplierNotes}
                                  >
                                    {item.supplierNotes}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${itemCalculations.cssClasses.stock}`}
                              data-testid={`badge-stock-${item.id}`}
                            >
                              {item.stock}
                              {item.reorderPoint &&
                                item.stock <= item.reorderPoint && (
                                  <span
                                    className="ml-1"
                                    title="Stock bajo punto de reorden"
                                  >
                                    ⚠️
                                  </span>
                                )}
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <span
                              className={
                                item.stock <= item.lowStockThreshold
                                  ? "text-destructive font-medium"
                                  : ""
                              }
                            >
                              {item.lowStockThreshold}
                            </span>
                          </TableCell>
                          <TableCell
                            className="truncate max-w-[70px] hidden xl:table-cell"
                            title={item.batchNumber || "Sin lote"}
                          >
                            {item.batchNumber ? (
                              <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                                {item.batchNumber}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden xl:table-cell">
                            {itemCalculations.expiryStatus !== "none" ? (
                              <span
                                className={itemCalculations.cssClasses.expiry}
                                title={
                                  itemCalculations.daysUntilExpiry !== null
                                    ? `Caduca en ${itemCalculations.daysUntilExpiry} días`
                                    : "Sin fecha de caducidad"
                                }
                              >
                                {itemCalculations.expiryStatus === "expired"
                                  ? "Vencido"
                                  : itemCalculations.expiryStatus === "warning"
                                    ? `${itemCalculations.daysUntilExpiry}d ⚠️`
                                    : itemCalculations.expiryStatus === "ok"
                                      ? `${itemCalculations.daysUntilExpiry}d`
                                      : itemCalculations.daysUntilExpiry !==
                                          null
                                        ? `${itemCalculations.daysUntilExpiry}d`
                                        : "—"}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <EditDialog
                                item={{
                                  ...item,
                                  updatedAt: new Date().toISOString(),
                                  supplierName: null,
                                  supplierContact: null,
                                  manufactureDate: null,
                                  id: item.id,
                                  shopId: item.shopId,
                                  productId: item.productId,
                                  price: item.price,
                                  stock: item.stock,
                                  lowStockThreshold: item.lowStockThreshold,
                                  acquisitionCost: item.acquisitionCost || null,
                                  profitMargin: item.profitMargin || null,
                                  supplierId: item.supplierId || null,
                                  supplierNotes: item.supplierNotes || null,
                                  batchNumber: item.batchNumber || null,
                                  purchaseDate: item.purchaseDate || null,
                                  expiryDate: item.expiryDate || null,
                                  minimumOrderQuantity:
                                    item.minimumOrderQuantity || null,
                                  leadTimeDays: item.leadTimeDays || null,
                                  dailySalesRate: item.dailySalesRate || null,
                                  reorderPoint: item.reorderPoint || null,
                                  suggestedPrice: item.suggestedPrice || null,
                                  averageCost: (item as any).averageCost ?? null,
                                  lastReceiptCost: (item as any).lastReceiptCost ?? null,
                                }}
                                productName={product?.name || "Producto"}
                                productCategory={product?.category || ""}
                                isOfflineMode={isOfflineMode}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          {/* Información de columnas para móvil */}
          <ColumnInfo />
        </CardContent>
      </Card>

      <AddProductDialog
        open={showAddProduct}
        onOpenChange={setShowAddProduct}
        mutation={createProductMutation}
        isOfflineMode={isOfflineMode}
        planCan={can}
      />

      {/* Paginación para gestión de inventario */}
      {totalPages > 1 && (
        <div className="mt-8 pt-6 border-t">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600">
              Mostrando{" "}
              <span className="font-medium">
                {(currentPage - 1) * itemsPerPage + 1}
              </span>{" "}
              -
              <span className="font-medium">
                {" "}
                {Math.min(currentPage * itemsPerPage, inventoryTotal)}
              </span>{" "}
              de
              <span className="font-medium"> {inventoryTotal}</span> productos
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm mb-2 sm:mb-0"
                disabled={isLoading || isOfflineMode}
              >
                <option value="20">20 por página</option>
                <option value="50">50 por página</option>
                <option value="100">100 por página</option>
              </select>

              <div className="flex items-center justify-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1 || isLoading}
                  className="h-8 w-8 p-0"
                >
                  ←
                </Button>

                <span className="text-sm text-gray-600 px-2 min-w-[100px] text-center">
                  Página {currentPage} de {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || isLoading}
                  className="h-8 w-8 p-0"
                >
                  →
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL GLOBAL DE PROVEEDOR ============ */}
      <SupplierModal
        open={supplierModalOpen}
        onOpenChange={setSupplierModalOpen}
        shopId={currentShop?.id || ""}
        supplier={selectedSupplier}
        mode={supplierModalMode}
        onSuccess={() => {
          // Invalidar queries para actualizar datos
          queryClient.invalidateQueries({
            queryKey: ["shopSuppliers", currentShop?.id],
          });
          queryClient.invalidateQueries({
            queryKey: ["inventoryReport", currentShop?.id],
          });

          // También puedes mostrar un toast de éxito
          toast({
            title: "Proveedor guardado",
            description: "El proveedor se ha guardado correctamente.",
            duration: 3000,
          });
        }}
      />
    </PageContainer>
  );
}
